/**
 * Filters Toggle Module
 * Manages expand/collapse state of filters with cookie persistence
 */

class FiltersToggleManager {
    constructor() {
        this.toggleBtn = null;
        this.mobileToggleBtn = null; // .filter-button with #filters-menu
        this.filtersDiv = null;
        this.filterCounter = null; // .fnd-filter-counter badge
        this.cookieName = 'filters_expanded';
        this.cookieExpireDays = 365;
        this.bsCollapse = null;
        this.isAnimating = false;
    }

    initialize() {
        this.toggleBtn = document.getElementById('filters-toggle');
        this.mobileToggleBtn = document.querySelector('.filter-button'); // Mobile button with #filters-menu
        this.filtersDiv = document.getElementById('filters-div');
        this.filterCounter = document.querySelector('.fnd-filter-counter');

        if (!this.filtersDiv) return;

        // Load saved state from cookie/localStorage (default: collapsed)
        let savedState = this.getCookie(this.cookieName);
        if (savedState == null && typeof window !== 'undefined' && window.localStorage) {
            try { savedState = localStorage.getItem(this.cookieName); } catch(_) {}
        }
        const isExpanded = savedState ? savedState === 'true' : false;

        // Apply initial state (force without animation)
        if (isExpanded) {
            this.filtersDiv.classList.add('show');
            if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
            if (this.mobileToggleBtn) this.mobileToggleBtn.setAttribute('aria-expanded', 'true');
        } else {
            this.filtersDiv.classList.remove('show');
            if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
            if (this.mobileToggleBtn) this.mobileToggleBtn.setAttribute('aria-expanded', 'false');
        }

        // Update badge state based on initial filters state
        // Delay to ensure filterController is initialized
        setTimeout(() => {
        this.updateBadgeState(!isExpanded);
        }, 300);

        // Create/reuse collapse instance
        this.ensureInstance();

        // Bind toggle event for desktop button (guard reentrancy)
        if (this.toggleBtn) {
        this.onToggleClick = (e) => {
            if (this.isAnimating) { e.preventDefault(); return; }
            this.toggle();
        };
        this.toggleBtn.removeEventListener('click', this.onToggleClick);
        this.toggleBtn.addEventListener('click', this.onToggleClick, { passive: true });
        }

        // Initialization logging only in debug mode
    }

    toggle() {
        const isExpanded = this.filtersDiv.classList.contains('show');
        this.isAnimating = true;
        if (isExpanded) {
            this.collapseFilters(true);
        } else {
            this.expandFilters(true);
        }
        // Safety unlock in case events don't fire
        setTimeout(() => { this.isAnimating = false; }, 400);
    }

    expandFilters(animate = true) {
        // CRITICAL: Hide desktop counter when filters are expanded (regardless of filter count)
        const desktopCounter = document.querySelector('.fnd-filter-counter-desktop');
        if (desktopCounter) {
            desktopCounter.style.display = 'none';
            desktopCounter.style.visibility = 'hidden';
            desktopCounter.classList.add('d-none');
        }
        if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
        if (this.mobileToggleBtn) this.mobileToggleBtn.setAttribute('aria-expanded', 'true');
        
        if (animate) {
            this.ensureInstance();
            this.bsCollapse.show();
        } else {
            this.filtersDiv.classList.add('show');
        }

        // Remove bg-warning when filters are expanded
        this.updateBadgeState(false);

        this.setCookie(this.cookieName, 'true', this.cookieExpireDays);
        try { localStorage.setItem(this.cookieName, 'true'); } catch(_) {}

        if (window.HyperLog) {
            window.HyperLog.debug('[FiltersToggle] Filters expanded');
        }
    }

    collapseFilters(animate = true) {
        if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
        if (this.mobileToggleBtn) this.mobileToggleBtn.setAttribute('aria-expanded', 'false');
        
        if (animate) {
            this.ensureInstance();
            this.bsCollapse.hide();
        } else {
            this.filtersDiv.classList.remove('show');
        }

        // Add bg-warning when filters are collapsed
        this.updateBadgeState(true);
        
        // CRITICAL: Update filter counter to show badge if filters are active
        // Use setTimeout to ensure DOM state is updated after collapse animation
        setTimeout(() => {
            if (window.filterController && typeof window.filterController.updateFilterCounter === 'function') {
                window.filterController.updateFilterCounter();
            }
        }, 100);

        this.setCookie(this.cookieName, 'false', this.cookieExpireDays);
        try { localStorage.setItem(this.cookieName, 'false'); } catch(_) {}

        if (window.HyperLog) {
            window.HyperLog.debug('[FiltersToggle] Filters collapsed');
        }
    }

    // Update badge bg-warning class based on filters state
    // collapsed: true = add bg-warning, false = remove bg-warning
    updateBadgeState(collapsed) {
        if (!this.filterCounter) return;
        if (collapsed) {
            this.filterCounter.classList.add('bg-warning');
        } else {
            this.filterCounter.classList.remove('bg-warning');
        }
        
        // Update desktop badge visibility
        const desktopCounter = document.querySelector('.fnd-filter-counter-desktop');
        if (desktopCounter) {
            // Verify actual DOM state (more reliable than parameter)
            const filtersDiv = document.getElementById('filters-div');
            const isActuallyCollapsed = filtersDiv && !filtersDiv.classList.contains('show');
            
            // Get filter count from mobile badge or calculate from filterController
            let countText = '';
            let count = 0;
            if (this.filterCounter && this.filterCounter.textContent) {
                countText = this.filterCounter.textContent;
                count = parseInt(countText) || 0;
            } else if (window.filterController && typeof window.filterController.updateFilterCounter === 'function') {
                // Trigger update to get current count
                window.filterController.updateFilterCounter();
                countText = desktopCounter.textContent || '';
                count = parseInt(countText) || 0;
            }
            desktopCounter.textContent = countText;
            
            // CRITICAL: Hide badge if no filters are active OR filters are expanded
            if (count === 0 || !countText || !isActuallyCollapsed) {
                desktopCounter.style.display = 'none';
                desktopCounter.style.visibility = 'hidden';
                desktopCounter.classList.add('d-none');
            } else {
                // Show badge ONLY when: filters are collapsed AND there are active filters
                if (isActuallyCollapsed && count > 0) {
                    desktopCounter.style.display = 'block';
                    desktopCounter.style.visibility = 'visible';
                    desktopCounter.classList.remove('d-none');
                } else {
                    desktopCounter.style.display = 'none';
                    desktopCounter.style.visibility = 'hidden';
                    desktopCounter.classList.add('d-none');
                }
            }
        }
    }

    getCookie(name) {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    setCookie(name, value, days) {
        let expires = '';
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
    }

    ensureInstance() {
        if (!this.filtersDiv) return;
        try {
            this.bsCollapse = bootstrap.Collapse.getInstance(this.filtersDiv) || new bootstrap.Collapse(this.filtersDiv, { toggle: false });
        } catch(_) {
            this.bsCollapse = new bootstrap.Collapse(this.filtersDiv, { toggle: false });
        }
    }

    // Keep aria-expanded in sync with actual collapse events
    // Also saves state when changed via Bootstrap collapse (e.g., mobile button)
    bindCollapseSync() {
        if (!this.filtersDiv) return;
        
        // Handle expand event
        this.filtersDiv.addEventListener('shown.bs.collapse', () => {
            if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
            if (this.mobileToggleBtn) this.mobileToggleBtn.setAttribute('aria-expanded', 'true');
            this.isAnimating = false;
            // CRITICAL: Hide desktop counter when filters are expanded (regardless of filter count)
            const desktopCounter = document.querySelector('.fnd-filter-counter-desktop');
            if (desktopCounter) {
                desktopCounter.style.display = 'none';
                desktopCounter.style.visibility = 'hidden';
                desktopCounter.classList.add('d-none');
            }
            // Remove bg-warning when filters are expanded
            this.updateBadgeState(false);
            // Save state when expanded via Bootstrap collapse
            this.setCookie(this.cookieName, 'true', this.cookieExpireDays);
            try { localStorage.setItem(this.cookieName, 'true'); } catch(_) {}
        });
        
        // Handle collapse event
        this.filtersDiv.addEventListener('hidden.bs.collapse', () => {
            if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
            if (this.mobileToggleBtn) this.mobileToggleBtn.setAttribute('aria-expanded', 'false');
            this.isAnimating = false;
            // Add bg-warning when filters are collapsed
            this.updateBadgeState(true);
            // CRITICAL: Update filter counter to show badge if filters are active
            if (window.filterController && typeof window.filterController.updateFilterCounter === 'function') {
                window.filterController.updateFilterCounter();
            }
            // Save state when collapsed via Bootstrap collapse
            this.setCookie(this.cookieName, 'false', this.cookieExpireDays);
            try { localStorage.setItem(this.cookieName, 'false'); } catch(_) {}
        });
    }
}

// Auto-initialize
if (typeof window !== 'undefined') { window.FiltersToggleManager = FiltersToggleManager; }

export default FiltersToggleManager;

